import * as React from 'react'

import { EventSourcePolyfill } from 'event-source-polyfill'

import { authTokenLocalStorageKey } from '@/lib/api/lib/client'

type Options<T> = {
    /** Handles one parsed message; `close` ends the stream for good. */
    onMessage: (data: T, close: () => void) => void
    onOpen?: () => void
    /**
     * Delay before reconnecting after the nth failure in a row (0-based), or
     * null to give up. A successful open starts the count again.
     */
    retryDelay: (failures: number) => number | null
}

/**
 * One authenticated server-sent event stream: parses messages, skips
 * keepalives and reconnects per `retryDelay`. No url means no stream; a new
 * url starts over. Handlers may change between renders without reconnecting.
 */
export function useEventStream<T>(
    url: string | undefined,
    options: Options<T>
) {
    const handlers = React.useRef(options)
    React.useEffect(() => {
        handlers.current = options
    })
    const [connected, setConnected] = React.useState(false)
    // Failures in a row: the ref drives retries, the state the UI.
    const [failures, setFailures] = React.useState(0)
    const failed = React.useRef({ url, count: 0 })
    // Bumped to reconnect.
    const [attempt, setAttempt] = React.useState(0)

    React.useEffect(() => {
        if (failed.current.url !== url) {
            failed.current = { url, count: 0 }
            setFailures(0)
        }
        if (!url) return
        let retry: ReturnType<typeof setTimeout> | undefined
        const source = new EventSourcePolyfill(url, {
            headers: {
                Authorization:
                    'Bearer ' +
                    (localStorage.getItem(authTokenLocalStorageKey) || '')
            }
        })
        const close = () => {
            source.close()
            setConnected(false)
        }
        source.onopen = () => {
            failed.current.count = 0
            setConnected(true)
            setFailures(0)
            handlers.current.onOpen?.()
        }
        source.onmessage = (event: { data: string }) => {
            let data: T & { keepalive?: boolean }
            try {
                data = JSON.parse(event.data)
            } catch {
                return
            }
            if (!data || data.keepalive) return
            try {
                handlers.current.onMessage(data, close)
            } catch (error) {
                console.error('[SSE] message handler failed:', error)
            }
        }
        source.onerror = () => {
            close()
            const n = failed.current.count++
            setFailures(n + 1)
            const delay = handlers.current.retryDelay(n)
            if (delay !== null) {
                retry = setTimeout(() => setAttempt((n) => n + 1), delay)
            }
        }
        return () => {
            clearTimeout(retry)
            close()
        }
    }, [url, attempt])

    return { connected, failures }
}
